/**
 * Casos do cliente no dossiê: um cartão por caso, com o essencial.
 *
 * Nome do caso, número CNJ (clique copia), vara e, à direita, a etiqueta de
 * status (clique altera, com confirmação) e o interruptor Em andamento/Arquivado.
 * O número interno do escritório não aparece. Arquivados ficam recolhidos.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, FolderOpen, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { CopyableProcessNumber } from '@/components/ui/CopyableProcessNumber'
import { CaseArchiveSwitch, CaseStatusControl } from '@/components/cases/CaseStatusControl'
import { Case, CaseStatus } from '@/types/case'
import { cn } from '@/utils/cn'

export interface ClientCaseListProps {
  cases: Case[]
  onCaseChanged: (updated: Case) => void
  onCreateCase?: () => void
}

function ClientCaseCard({ item, onCaseChanged }: { item: Case; onCaseChanged: (updated: Case) => void }) {
  const archived = item.status === CaseStatus.ARCHIVED
  return (
    <Link
      to={`/cases/${item.id}`}
      className={cn(
        'flex flex-col gap-2 rounded-lg border bg-card p-4 transition-colors hover:bg-muted/60 sm:flex-row sm:items-start sm:justify-between',
        archived && 'opacity-70',
      )}
    >
      <div className="min-w-0 flex-1">
        <h3 className="break-words text-base font-semibold">{item.title}</h3>
        {(item.courtNumber || item.courtName) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {item.courtNumber && <CopyableProcessNumber number={item.courtNumber} className="text-sm text-foreground/80" />}
            {item.courtName && <span className="min-w-0 truncate">{item.courtName}</span>}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
        <CaseStatusControl caseId={item.id} caseTitle={item.title} status={item.status} onChanged={onCaseChanged} />
        <CaseArchiveSwitch caseId={item.id} caseTitle={item.title} status={item.status} onChanged={onCaseChanged} />
      </div>
    </Link>
  )
}

export function ClientCaseList({ cases, onCaseChanged, onCreateCase }: ClientCaseListProps) {
  const [showArchived, setShowArchived] = useState(false)
  const current = cases.filter((c) => c.status !== CaseStatus.ARCHIVED)
  const archived = cases.filter((c) => c.status === CaseStatus.ARCHIVED)

  if (cases.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-8 text-center">
        <FolderOpen className="size-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Nenhum caso deste cliente.</p>
        {onCreateCase && (
          <Button size="sm" onClick={onCreateCase}><Plus className="size-4" /> Novo caso</Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {current.length === 0 && <p className="text-sm text-muted-foreground">Todos os casos deste cliente estão arquivados.</p>}
      {current.map((item) => <ClientCaseCard key={item.id} item={item} onCaseChanged={onCaseChanged} />)}
      {archived.length > 0 && (
        <Collapsible open={showArchived} onOpenChange={setShowArchived}>
          <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ChevronDown className={cn('size-4 transition-transform', showArchived && 'rotate-180')} aria-hidden="true" />
            Arquivados ({archived.length})
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 flex flex-col gap-3">
            {archived.map((item) => <ClientCaseCard key={item.id} item={item} onCaseChanged={onCaseChanged} />)}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}
